// src/pages/Legal/TermosDeUsoPage.jsx

import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Stack,
  Button,
} from '@mui/material';
import {
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  AccountBalance as AccountBalanceIcon,
  Policy as PolicyIcon,
  ContactSupport as ContactSupportIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';

// Componente para seções dos termos
const TermSection = ({ title, icon: Icon, children }) => (
  <Paper sx={{ p: 3, mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Icon color="primary" sx={{ mr: 2, fontSize: 28 }} />
      <Typography variant="h6">{title}</Typography>
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

TermSection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
};

const TermosDeUsoPage = () => {
  // Data atual formatada
  const dataAtual = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 6,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom>
            Termos de Uso
          </Typography>
          <Typography variant="h6">
            Regras e condições para utilização da plataforma ZenKroll
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon />
            <Typography variant="subtitle1">
              Última actualização: {dataAtual}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ flex: 1, mb: 4 }}>
        {/* Alerta de Aceitação */}
        <Alert severity="info" sx={{ mb: 4 }}>
          Ao utilizar nossa plataforma, você concorda com estes termos de uso. Por favor, leia-os atentamente.
        </Alert>

        {/* Definições */}
        <TermSection title="Definições" icon={AssignmentIcon}>
          <List>
            <ListItem>
              <ListItemIcon><SchoolIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Instituição"
                secondary="Instituições de ensino cadastradas que oferecem cursos e campanhas na plataforma"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><PersonIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Estudante"
                secondary="Usuários que se cadastram para realizar pré-inscrições em cursos"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><AssignmentIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Campanha"
                secondary="Período de pré-inscrições aberto por uma instituição para determinados cursos"
              />
            </ListItem>
          </List>
        </TermSection>

        {/* Uso da Plataforma */}
        <TermSection title="Uso da Plataforma" icon={CheckCircleIcon}>
          <Typography paragraph>
            Ao utilizar o ZenKroll, você concorda em:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Fornecer informações verdadeiras e actualizadas"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Manter a segurança de suas credenciais de acesso"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Respeitar os prazos e procedimentos estabelecidos"
              />
            </ListItem>
          </List>
        </TermSection>

        {/* Responsabilidades das Instituições */}
        <TermSection title="Responsabilidades das Instituições" icon={SchoolIcon}>
          <List>
            <ListItem>
              <ListItemIcon><AssignmentIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Gestão de Campanhas"
                secondary="Criar e gerir campanhas de pré-inscrição com informações precisas"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><ScheduleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Prazos"
                secondary="Respeitar os prazos estabelecidos para análise das pré-inscrições"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Dados"
                secondary="Manter a confidencialidade dos dados dos estudantes"
              />
            </ListItem>
          </List>
        </TermSection>

        {/* Responsabilidades dos Estudantes */}
        <TermSection title="Responsabilidades dos Estudantes" icon={PersonIcon}>
          <List>
            <ListItem>
              <ListItemIcon><AssignmentIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Documentação"
                secondary="Fornecer documentos válidos e atualizados durante a pré-inscrição"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><PaymentIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Pagamentos"
                secondary="Efetuar os pagamentos dentro dos prazos estabelecidos"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><ScheduleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Prazos"
                secondary="Acompanhar e respeitar os prazos das campanhas"
              />
            </ListItem>
          </List>
        </TermSection>

        {/* Pagamentos */}
        <TermSection title="Pagamentos e Taxas" icon={PaymentIcon}>
          <Typography paragraph>
            Condições gerais sobre pagamentos:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><AccountBalanceIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Formas de Pagamento"
                secondary="As formas de pagamento aceitas são definidas por cada instituição"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><ScheduleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Prazos"
                secondary="O não cumprimento dos prazos de pagamento pode resultar no cancelamento da pré-inscrição"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><AssignmentIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Comprovantes"
                secondary="Os comprovantes de pagamento devem ser mantidos para eventual verificação"
              />
            </ListItem>
          </List>
        </TermSection>

        {/* Condutas Proibidas */}
        <TermSection title="Condutas Proibidas" icon={BlockIcon}>
          <Typography paragraph color="error">
            É expressamente proibido:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
              <ListItemText 
                primary="Fornecer informações falsas"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
              <ListItemText 
                primary="Usar documentos de terceiros sem autorização"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
              <ListItemText 
                primary="Tentar burlar o sistema de pagamentos"
              />
            </ListItem>
          </List>
        </TermSection>

        {/* Privacidade e Dados */}
        <TermSection title="Privacidade e Proteção de Dados" icon={SecurityIcon}>
          <Typography paragraph>
            O tratamento de dados pessoais está sujeito à nossa Política de Privacidade.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<PolicyIcon />}
            component={Link}
            to="/privacidade"
            sx={{ mb: 2 }}
          >
            Ver Política de Privacidade
          </Button>
        </TermSection>

        {/* Alterações nos Termos */}
        <TermSection title="Alterações nos Termos" icon={AssignmentIcon}>
          <Typography paragraph>
            O ZenKroll se reserva o direito de alterar estes termos a qualquer momento.
            Alterações significativas serão comunicadas através da plataforma.
          </Typography>
        </TermSection>

        {/* Contato */}
        <TermSection title="Contato" icon={ContactSupportIcon}>
          <Typography paragraph>
            Para dúvidas sobre estes termos:
          </Typography>
          <Stack spacing={2}>
            <Button
              variant="contained"
              startIcon={<ContactSupportIcon />}
              component={Link}
              to="/suporte"
            >
              Central de Suporte
            </Button>
            <Typography variant="body2" color="text.secondary">
              Email: termos@zenkroll.co.mz
            </Typography>
          </Stack>
        </TermSection>
      </Container>

      <Footer />
    </Box>
  );
};

TermosDeUsoPage.propTypes = {
  // Nenhuma prop recebida diretamente pelo componente
};

export default TermosDeUsoPage;
